import { Box, Typography } from "@mui/material";
import LogTable, { SortOn } from "./logTable";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { DatePickerPopUp } from "../../../components/molecules/datepickers/datePickerPopUp";
import { FetchFromBackoffice, PostQueryBackoffice } from "../../../utils/request";
import { useSearchParams } from "react-router-dom";
import { DataLoadingText } from "../../../components/atoms/text/dataLoadingText";
import { CustomTimePicker } from "../../../components/molecules/datepickers/timePicker";
import theme from "../../../theme";
import { TagButton } from "../../../components/molecules/datepickers/tagButtons";
import { LevelType } from "../../../components/molecules/logs/logLevelDisplay";
import { MultipleSelectInput, SelectOption } from "../../../components/atoms/inputs/multipleSelectInput";
import { LogLevelIcon } from "../../../components/molecules/logs/logLevelIcon";

export type LogItem = {
    record: string,
    level: LevelType,
    source: string,
    telematicsId: string,
    timestamp: string
}

type FetchingState = 'loading' | 'completed' | 'failed'

export const LogsSection = ({ serialId }: { serialId: string }) : React.ReactElement[]=> {
    const [searchParams, setSearchParams] = useSearchParams()
    const errorLevelsRef = useRef<LevelType[] | null>(null)
    const from = searchParams.get('from')
    const until = searchParams.get('until')
    const count = Number(searchParams.get('count') || 10);
    const page = Number(searchParams.get('page') || 0);
    const [pageState, setPageState] = useState(page);
    const [countState, setCountState] = useState(count);
    const descending = searchParams.get('order') === "asc" ? false : true
    const levelFilters = useMemo(() => searchParams.getAll('level') || [], [searchParams])
    //date pickers
    const [inputStartDate, setInputStartDate] = useState<DateTime | undefined>(until ? DateTime.fromISO(until) : undefined)
    const [inputEndDate, setInputEndDate] = useState<DateTime | undefined>(from ? DateTime.fromISO(from) : undefined)
    const [isStartDatePickerOpen,setStartDatePickerOpen] = useState<boolean>(false)
    const [isEndDatePickerOpen,setEndDatePickerOpen] = useState<boolean>(false)

    //time pickers
    const [isStartTimePickerOpen, setStartTimePickerOpen] = useState<boolean>(false)
    const [isEndTimePickerOpen, setEndTimePickerOpen] = useState<boolean>(false)

    const [logs, setLogs] = useState<LogItem[]>([])
    const [total, setTotal] = useState<number>(0)
    const [sortingOn, setSortingOn] = useState<SortOn>('timestamp')
    const [fetchingState, setFetchingState] = useState<FetchingState>('loading')
    const [delayedFetchingState, setDelayedFetchingState] = useState<FetchingState>('completed')

    const [noLogs, setNoLogs] = useState<boolean>(true)
    const [isEndBeforeStart, setEndBeforeStart] = useState<boolean>(false)
    const startDatePresets: TagButton[] = [
        {
          name: "Earliest entry",
          id: "earliest",
          action: () => {
            setInputStartDate(undefined)
            setStartDatePickerOpen(false);
          },
          default: true
        },
      ];

    const endDatePresets: TagButton[] = [
        {
          name: "Latest entry",
          id: "latest",
          action: () => {
            setInputEndDate(undefined)
            setEndDatePickerOpen(false);
          },
          default: true
        },
      ];

    const getErrorLevels = async () : Promise<string[]> => {
        const errorLevels = await FetchFromBackoffice<string[]>('logs/levels')
        return errorLevels
    }

    useEffect(() => {
        getErrorLevels().then(levels => {
        errorLevelsRef.current = levels as LevelType[];
        });
    }, []);

    //updates page and count at the same time to avoid that data gets dropped
    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', pageState.toString());
        newParams.set('count', countState.toString());
        setSearchParams(newParams, { replace: true });
    }, [pageState, countState, searchParams]);

    //displays loading icon if fetching logs is slow
    useEffect(() => {
        let handler : NodeJS.Timeout | null = null
        if (fetchingState === 'loading') {
            handler = setTimeout(() => {
                setDelayedFetchingState('loading')
            }, 1000)
        } else {
            setDelayedFetchingState(fetchingState)
        }
        return () => {
            if (handler) clearTimeout(handler)
        }
    }, [fetchingState])

    useEffect(() => {
        setFetchingState('loading')
        const handler = setTimeout(() => {
            var requestObject = {
                "typeFilter": levelFilters,
                "query": "",
                "sortBy": [
                    {
                        "property": "timestamp",
                        "direction": descending ? "desc" : "asc"
                    }
                ]
            }

            if (inputStartDate) {
                requestObject = { ...requestObject, ...{ "until": inputStartDate} }
            }
            if (inputEndDate) {
                requestObject = { ...requestObject, ...{ "from": inputEndDate } }
            }
            const fetchData = async () => {
                const results = await PostQueryBackoffice(`v2/machines/${serialId}/logs?Page=${page}&Count=${count}`, requestObject)
                const items: LogItem[] = [...results.items?.map(
                    (r): LogItem => {
                        return {
                            record: r.record,
                            level: r.level,
                            source: r.source,
                            telematicsId: r.telematicsId,
                            timestamp: r.timestamp
                        }

                    }
                )]

                if (items === undefined || items.length === 0) {
                    setFetchingState('failed')
                }
                else {
                    setFetchingState('completed')
                }
                setTotal(results?.total)
                setLogs(items)

                if (total > 0) {
                    setNoLogs(false)
                }
            }
            fetchData()

        }, 500)
        return () => clearTimeout(handler)
        
        
    }, [page, count, inputStartDate, inputEndDate, descending, levelFilters])

    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        if (inputStartDate) {
            newParams.set('until', inputStartDate.toISO());
        }
        else {
            newParams.delete('until')
        }
        setPageState(0)
        setSearchParams(newParams, {replace: true}); 

        if(inputStartDate && inputEndDate && inputEndDate < inputStartDate){
            setEndBeforeStart(true)
        }
        else{
            setEndBeforeStart(false)
        }
    }, [inputStartDate])

    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        if (inputEndDate) {
            newParams.set('from', inputEndDate.toISO());
        }
        else {
            newParams.delete('from')
        }
        setPageState(0)
        setSearchParams(newParams, {replace: true}); 

        if(inputStartDate && inputEndDate && inputEndDate < inputStartDate){
            setEndBeforeStart(true)
        }
        else{
            setEndBeforeStart(false)
        }
    },[inputEndDate])

    const updateStartDate = (newStartDate:DateTime) =>{ 
        const date = newStartDate ? (inputStartDate ?? newStartDate).set({
            year: newStartDate.year,
            month: newStartDate.month,
            day: newStartDate.day,
        }) : undefined
        setInputStartDate(date);
    }

    const updateStartTime = (newStartTime: DateTime) => {
        const time = newStartTime ? inputStartDate?.set({
            hour: newStartTime.hour,
            minute: newStartTime.minute,
        }) : undefined
        setInputStartDate(time);
    }

    const updateEndDate = (newEndDate:DateTime) =>{ 
        //if date was undefined but now set, the time should default to 23:59
        const date = newEndDate ? (inputEndDate ? inputEndDate.set({
            year: newEndDate.year,
            month: newEndDate.month,
            day: newEndDate.day,
        }) : newEndDate.set({
            year: newEndDate.year,
            month: newEndDate.month,
            day: newEndDate.day,
            hour: 23,
            minute: 59,
        })): undefined
        setInputEndDate(date);
    }

    const updateEndTime = (newEndTime: DateTime) => {
        const time = newEndTime ? inputEndDate?.set({
            hour: newEndTime.hour,
            minute: newEndTime.minute,
        }) : undefined
        setInputEndDate(time);
    }

    const updateDescending = (newDescending: boolean) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('order', newDescending ? "desc" : "asc");
        setSearchParams(newParams, {replace: true});
    }

    const updateLevelFilters = (newFilters: string[]) => {
        const newParams = new URLSearchParams(searchParams);
        if(!newFilters || (levelFilters.length === newFilters.length && levelFilters.every((v, i) => v === newFilters[i]))){
            return
        }
        newParams.delete('level')
        newFilters.forEach(level => newParams.append('level', level))
        setSearchParams(newParams, {replace: true});
    }

    const createSelectOptionList = (list :LevelType[]) : SelectOption[] => {
        return (list.map(item => ({
            item: item,
            icon: <LogLevelIcon level={item}/>
        })))

    }

    return(
        [
            <Box sx= {{display: 'flex', flexDirection: 'row', alignItems: "center", gap:"4rem"}}>
                <Box sx= {{display: 'flex', flexDirection: 'row', alignItems: "flex-start", gap:"8px"}}>
                    <Box sx= {{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                        <DatePickerPopUp
                            date={inputStartDate}
                            onDatePicked={updateStartDate}
                            setOpen={setStartDatePickerOpen}
                            open={isStartDatePickerOpen}
                            text= {inputStartDate ? "" :"Earliest"}
                            error = {isEndBeforeStart}
                            presets={startDatePresets}
                            />
                        {
                            isEndBeforeStart && (
                                <Typography variant={"caption"} color={theme.palette.error.main}>End date before start date</Typography>
                            )
                        }
                    </Box> 
                    <CustomTimePicker
                        time={inputStartDate}
                        setTime={updateStartTime}
                        text={inputStartDate ? "" : "00:00"}
                        setOpen={setStartTimePickerOpen}
                        open={isStartTimePickerOpen}
                        />
                    <p>
                        —
                    </p>
                    <Box sx= {{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                        <DatePickerPopUp
                            date={inputEndDate}
                            onDatePicked={updateEndDate}
                            setOpen={setEndDatePickerOpen}
                            open={isEndDatePickerOpen}
                            text={inputEndDate ? "" : "Latest"}
                            error={isEndBeforeStart}
                            presets={endDatePresets}
                            />
                        {
                            isEndBeforeStart && (
                                <Typography variant={"caption"} color={theme.palette.error.main}>End date before start date</Typography>
                            )
                        }
                    </Box>
                    <CustomTimePicker
                        time={inputEndDate}
                        setTime={updateEndTime}
                        text={inputEndDate ? "" : "23:59"}
                        setOpen={setEndTimePickerOpen}
                        open={isEndTimePickerOpen}
                        />
                </Box> 
                <MultipleSelectInput
                    title="Log level"
                    options = {errorLevelsRef.current ? createSelectOptionList(errorLevelsRef.current) : []}
                    values={levelFilters}
                    onClose={updateLevelFilters}
                  
                />
            </Box>,
            <Box>
                {
                    fetchingState !== 'failed' ? (
                        delayedFetchingState === 'loading' ? (
                            <Box p={4}>
                                <DataLoadingText showSpinner text={'Loading logs ...'} />
                            </Box>
                        ) : (
                            <LogTable
                                logs={logs}
                                total={total}
                                countPerPage={count}
                                sortingOn={sortingOn}
                                descending={descending}
                                page={page}
                                setCountPerPage={setCountState}
                                setDescending={updateDescending}
                                setPage={setPageState}
                                setSortingOn={setSortingOn}
                            />)
                    ) : (
                        <Box p={4}>
                            <DataLoadingText text={noLogs ? 'No logs for this hub.' : 'No logs for this time interval.'} />
                        </Box>
                    )
                }
            </Box>
        ]
    )
  };